import React from 'react';
import { cn } from '@/lib/cn';
import { IconChevronRight } from '@repo/svgs';
import { Skeleton } from '@/components/ui/Skeleton';

export type ListCellTrailing = 'chevron' | 'none';

export interface ListCellProps extends React.HTMLAttributes<HTMLDivElement> {
  /** Leading icon element (24px). */
  leadingIcon?: React.ReactNode;
  /** Leading image/thumbnail (square, ~48px). Takes precedence over leadingIcon. */
  leadingImage?: string;
  /** Primary title text (14/20, normal). */
  title: string;
  /** Optional className for the title text. */
  titleClassName?: string;
  /** Optional subtitle text (12/16, normal). */
  subtitle?: string;
  /** Optional className for the subtitle text. */
  subtitleClassName?: string;
  /** Optional trailing title text (14/20, normal, right-aligned). */
  trailingTitle?: string;
  /** Optional trailing subtitle text (12/16, normal, right-aligned). */
  trailingSubtitle?: string;
  /** Trailing adornment. Defaults to 'chevron'. Pass ReactElement to override. */
  trailing?: ListCellTrailing | React.ReactElement;
}

export function ListCell({
  leadingIcon,
  leadingImage,
  title,
  titleClassName,
  subtitle,
  subtitleClassName,
  trailingTitle,
  trailingSubtitle,
  trailing = 'chevron',
  className,
  ...props
}: ListCellProps) {
  const trailingEl = React.useMemo(() => {
    if (React.isValidElement(trailing)) return trailing;
    if (trailing === 'none') return null;
    return <IconChevronRight className="w-3 h-3 icon-tertiary shrink-0" aria-hidden />;
  }, [trailing]);

  // If we have trailing title/subtitle, don't show the default chevron
  const hasTrailingText = trailingTitle || trailingSubtitle;
  const showTrailing = hasTrailingText ? trailing !== 'chevron' : trailing !== 'none';

  return (
    <div
      className={cn(
        'flex items-center gap-4 rounded-xl px-2 py-3 bg-primary cursor-pointer',
        'hover:bg-secondary active:bg-secondary transition-colors',
        'w-full',
        className,
      )}
      {...props}
    >
      {leadingImage ? (
        <div className="w-12 h-12 shrink-0 rounded-lg overflow-hidden bg-secondary">
          <img
            src={leadingImage}
            alt=""
            className="w-full h-full object-cover"
            onError={e => {
              // Fallback to placeholder if image fails to load
              (e.target as HTMLImageElement).src = '/listing-card-placeholder.png';
            }}
          />
        </div>
      ) : leadingIcon ? (
        <div className="w-6 h-6 shrink-0 flex items-center justify-center">{leadingIcon}</div>
      ) : null}

      <div className="flex-1 flex items-center self-stretch min-w-0">
        <div className="flex-1 flex flex-col gap-1 h-full items-start justify-center min-w-0">
          {hasTrailingText ? (
            <>
              <div className="flex gap-4 items-start w-full">
                <p className={cn('text-body-2 text-primary flex-1 min-w-0', titleClassName)}>
                  {title}
                </p>
                {trailingTitle && (
                  <p className="text-body-2 text-primary text-right whitespace-nowrap shrink-0">
                    {trailingTitle}
                  </p>
                )}
              </div>
              {(subtitle || trailingSubtitle) && (
                <div className="flex gap-4 items-center w-full">
                  <p
                    className={cn('text-caption text-secondary flex-1 min-w-0', subtitleClassName)}
                  >
                    {subtitle || ''}
                  </p>
                  {trailingSubtitle && (
                    <p className="text-caption text-secondary text-right whitespace-nowrap shrink-0">
                      {trailingSubtitle}
                    </p>
                  )}
                </div>
              )}
            </>
          ) : (
            <>
              <p className={cn('text-body-2 text-primary w-full', titleClassName)}>{title}</p>
              {subtitle ? (
                <p className={cn('text-caption text-secondary w-full', subtitleClassName)}>
                  {subtitle}
                </p>
              ) : null}
            </>
          )}
        </div>
      </div>

      {showTrailing && trailingEl ? <div className="shrink-0">{trailingEl}</div> : null}
    </div>
  );
}

export function ListCellSkeleton({ className }: { className?: string }) {
  return (
    <div className={cn('flex items-center gap-4 rounded-xl px-2 py-3 bg-primary', className)}>
      <Skeleton className="w-6 h-6 rounded" />
      <div className="min-w-0 flex-1 flex flex-col items-start justify-center gap-2">
        <Skeleton className="h-4 w-2/3 rounded" />
        <Skeleton className="h-3 w-1/3 rounded" />
      </div>
      <div className="shrink-0">
        <Skeleton className="w-3 h-3 rounded" />
      </div>
    </div>
  );
}

export default ListCell;
